<template>
    <article>
        <p>
            Usually, any metric refers to the node from which it's collected by hostname.
            However, when something goes wrong with a node, it can be tricky to determine the underlying cloud instance because
            the node name may be inconsistent with the instance name in the cloud console.
            Fortunately, this problem is not hard to solve since most cloud providers allow instances to retrieve their own metadata without
            the need for any special permissions.
        </p>

        <p>
            In this post, I'll explain how
            <a href="https://github.com/coroot/coroot-node-agent" target="_blank" rel="noopener noreferrer">node-agent</a>
            obtains instance metadata in different clouds.
        </p>

        <p>
            In order to identify which cloud an instance is running in, the agent uses the following heuristics:
        </p>

        <ul>
            <li>
                AWS Xen instances: the uuid from <var>/sys/hypervisor/uuid</var> has the "ec2" or "EC2" prefix
            </li>
            <li>
                AWS Nitro instances: the content of <var>/sys/class/dmi/id/board_vendor</var> is "Amazon EC2"
            </li>
            <li>
                GCP: the content of <var>/sys/class/dmi/id/board_vendor</var> is "Google"
            </li>
            <li>
                Azure: the content of <var>/sys/class/dmi/id/board_vendor</var> is "Microsoft Corporation"
            </li>
        </ul>

        <p>
            If the cloud provider is detected, the agent retrieves the instance metadata using the provider-specific API.
            Instance Metadata Services (IMDS) are usually available at <var>http://169.254.169.254</var>.
            The IPv4 block <var>169.254.0.0/16</var> is reserved for link-local addressing,
            this means that addresses from this block cannot be accessed from outside the host.
        </p>


        <p>
            As a result, the agent exports the <MetricLink name="node_cloud_info" exporter="node-agent"/> metric:
        </p>

        <pre v-hl.sh>
node_cloud_info{
    provider="&lt;AWS|GCP|Azure&gt;",
    account_id="&lt;the ID of the account which the instance belongs to&gt;",
    instance_id="&lt;the ID of the instance&gt;",
    instance_type="&lt;the type of instance&gt;",
    instance_life_cycle="&lt;the purchasing options of the instance (on-demand/spot/reserved)&gt;",
    region="&lt;the cloud region where the instance is running&gt;",
    availability_zone="&lt;the Availability Zone where the instance is running&gt;",
    availability_zone_id="&lt;the Availability Zone ID where the instance is running (AWS only)&gt;",
    local_ipv4="&lt;the local IPv4 address&gt;",
    public_ipv4="&lt;the public IPv4 address&gt;",
}
        </pre>

        <p>
            Below are my notes on the nuances of different clouds.
        </p>


        <h2>Amazon Web Services (AWS)</h2>

        <p>
            The full list of available metadata categories is available <a href="https://docs.aws.amazon.com/AWSEC2/latest/UserGuide/instancedata-data-categories.html" target="_blank" rel="noopener noreferrer">here</a>.
            To list available categories from within an EC2 instance, you can use the following command:
        </p>

        <pre v-hl.bash>
$ curl http://169.254.169.254/latest/meta-data/
ami-id
ami-launch-index
ami-manifest-path
block-device-mapping/
events/
hostname
identity-credentials/
instance-action
instance-id
instance-life-cycle
instance-type
local-hostname
local-ipv4
mac
metrics/
network/
placement/
profile
public-hostname
public-ipv4
public-keys/
reservation-id
security-groups
services/
        </pre>

        <p>
            To get the value of a specific variable, add its name to the URL. For example:
        </p>

        <pre v-hl.bash>
$ curl http://169.254.169.254/latest/meta-data/instance-id
i-0c6ec76f046843cd4

$ curl http://169.254.169.254/latest/meta-data/placement/availability-zone
us-east-1a
        </pre>

        <p>
            What's important to understand is that Availability Zone names don't map to the <a href="https://docs.aws.amazon.com/ram/latest/userguide/working-with-az-ids.html" target="_blank" rel="noopener noreferrer">same physical locations</a> across accounts.
            For example, <var>us-west-1a</var> in one account can point to the AZ with <var>ID=usw1-az3</var>,
            and to the AZ <var>ID=usw1-az1</var> in another account.

        </p>

        <v-img max-width="80%" class="ma-auto mb-6" src="/static/img/blog/aws_az.svg" />


        <p>
            Therefore, if it matters in your case, use the <var>availability_zone_id</var> label of the
            <MetricLink name="node_cloud_info" exporter="node-agent"/> metric instead of <var>availability_zone</var>.

        </p>

        <h2>Google Cloud Platform (GCP)</h2>

        <p>
            The documentation of the GCP Instance Metadata Service is available <a href="https://cloud.google.com/compute/docs/metadata/overview" target="_blank" rel="noopener noreferrer">here</a>.
            The service works similarly to AWS, except that each request must contain the <var>Metadata-Flavor: Google</var> header:
        </p>

        <pre v-hl.sh>
$ curl -H "Metadata-Flavor: Google" http://169.254.169.254/computeMetadata/v1/instance/
attributes/
cpu-platform
description
disks/
guest-attributes/
hostname
id
image
legacy-endpoint-access/
licenses/
machine-type
maintenance-event
name
network-interfaces/
preempted
remaining-cpu-time
scheduling/
service-accounts/
tags
virtual-clock/
zone
        </pre>

        <p>

            Another difference from AWS is that GCP doesn't have a separate variable for the name of the region in which an instance is working.
            The agent extracts the region name from the <var>zone</var> variable:
        </p>

        <pre v-hl.sh>
$ curl -H "Metadata-Flavor: Google" http://169.254.169.254/computeMetadata/v1/instance/zone
projects/&lt;account-id&gt;/zones/us-central1-a
        </pre>

        <p>
            For this example, the region is <var>us-central1</var>.
        </p>

        <p>
            As for the instance purchase options, GCP provides only the <var>preempted</var> flag.
            Depending on its value, the agent puts <var>on-demand</var> or <var>preemptible</var> to the <var>instance_life_cycle</var> label.
        </p>


        <h2>Microsoft Azure</h2>

        <p>
            The Azure Instance Metadata Service (IMDS) provides information about a VM in JSON <a href="https://docs.microsoft.com/en-us/azure/virtual-machines/linux/instance-metadata-service?tabs=linux#response-1" target="_blank" rel="noopener noreferrer">format</a>.
            Each request must contain the <var>Metadata:true</var> header and the <var>api-version</var> argument.
            The list of supported API versions is available at <var>http://169.254.169.254/metadata/versions</var>.
            Microsoft promises that versions will be supported indefinitely, so we hardcoded the latest available version into node-agent.
        </p>

        <p>
            Like other cloud providers, Azure has at least three Availability Zones in each region.
            However, if a VM is launched in the <var>single instance</var> mode,
            the metadata service doesn't report the Availability Zone in which this instance was launched.
        </p>

        <h2>Why it's important to understand where your instances run</h2>

        <p>
            An Availability Zone is an isolated infrastructure (one or more data centers) with independent power, cooling, networking, etc.
            Cloud providers usually suggest customers run their workloads in multiple AZs to be tolerant to failure of an individual zone.
            It does really increase fault tolerance, but don't forget that:
        </p>

        <ul>
            <li>Network latency between availability zones within the same region can be higher than within one particular zone.</li>
            <li>Data transfer between availability zones in the same region is paid, while data transfer within a zone is free.</li>
        </ul>

        <p>
            In any case, it is important to have an overview of how your applications are distributed across availability zones and
            <router-link :to="{name: 'blog', params: {name: 'building-a-service-map-using-ebpf'}}">communicate with each other</router-link>.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/cloud-metadata/availability-zones.png" />
        </v-card>

        <h2>Manually annotating nodes with placement metadata</h2>

        <p>
            If your infrastructure is built on bare metal servers or your cloud provider doesn't have a metadata service, you can
            set <var>provider</var>, <var>region</var>, and <var>availability_zone</var> manually using command-line arguments of node-agent.
            For instance, for a node in the Equinix datacenter <var>LA3</var> in Los Angeles, the arguments might be as follows:
        </p>

        <pre v-hl.bash>
coroot-node-agent --provider=equinix --region=us-west-la --availability-zone=us-west-la3
        </pre>

        <h2>Useful links</h2>

        <ul>
            <li>
                <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Get started with Coroot</router-link>
            </li>
            <li>
                <a href="https://github.com/coroot/coroot-node-agent" target="_blank" rel="noopener noreferrer">node-agent <v-icon dense>mdi-github</v-icon></a> (Apache-2.0 License)
            </li>
            <li>
                <router-link :to="{name: 'blog', params: {name: 'building-a-service-map-using-ebpf'}}">Building a service map using eBPF</router-link>
            </li>
        </ul>

    </article>
</template>

<script>
import MetricLink from "@/components/MetricLink";

export default {
    components: {MetricLink}
}
</script>

<style scoped>
</style>
